import type { HandledApiResponse } from "@entrilia/shared/api/axiosHelper";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Box, Button, Grid, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import adminApi from "../../../../api/adminApi";
import type { AssistantTaskChatStartKind } from "../../../../api/types/assistantTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import { getChatPath } from "../assistantPaths";
import DeleteTaskDialog from "./DeleteTaskDialog";
import TaskAttentionBox from "./TaskAttentionBox";
import TaskFacts from "./TaskFacts";
import TaskRuns from "./TaskRuns";
import TaskTitle from "./TaskTitle";

const REFRESH_MS = 30_000;

const runAssistantTask = withErrorHandling(adminApi.runAssistantTask);
const pauseAssistantTask = withErrorHandling(adminApi.pauseAssistantTask);
const resumeAssistantTask = withErrorHandling(adminApi.resumeAssistantTask);
const dismissAssistantTaskProblem = withErrorHandling(adminApi.dismissAssistantTaskProblem);
const startAssistantTaskChat = withErrorHandling(adminApi.startAssistantTaskChat);

interface TaskPageContentProps {
  taskId: string;
}

const TaskPageContent = ({ taskId }: TaskPageContentProps) => {
  usePageTitle("Your Assistant");
  const { clientCode } = useClientContext();
  const navigate = useNavigate();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const getTask = useCallback((signal?: AbortSignal) => adminApi.getAssistantTask(taskId, signal), [taskId]);
  const [details, fetchError, { fetchSilent }] = useFetch(getTask);
  useSetInterval(fetchSilent, REFRESH_MS);

  if (fetchError || !details) {
    if (fetchError) {
      logError(fetchError, "[AssistantTaskPage] getAssistantTask");
    }

    return (
      <>
        <GeneralPageHeader showDefaultBackButtonTo={getChatPath(clientCode)} title="Task" />
        {fetchError ? <DataLoadingFailed title="Failed to load the task" /> : <InlineLoader />}
      </>
    );
  }

  const { task } = details;

  const refresh = async () => {
    const error = await fetchSilent();
    if (error) {
      logError(error, "[AssistantTaskPage] getAssistantTask");
    }
  };

  const runAction = async <T,>(
    apiCall: (id: string) => Promise<HandledApiResponse<T>>,
    operation: string,
    successText?: string
  ) => {
    setBusy(true);
    const [, error] = await apiCall(task.taskId);
    if (error) {
      setBusy(false);
      logError(error, `[AssistantTaskPage] ${operation}`);
      sendNotificationError(error.message);
      return;
    }

    if (successText) {
      sendNotification(successText);
    }
    await refresh();
    setBusy(false);
  };

  const startTaskChat = async (kind: AssistantTaskChatStartKind) => {
    setBusy(true);
    const [response, error] = await startAssistantTaskChat(task.taskId, { kind });
    if (error) {
      setBusy(false);
      logError(error, "[AssistantTaskPage] startAssistantTaskChat");
      sendNotificationError(error.message);
      return;
    }

    navigate(getChatPath(clientCode, response.threadId));
  };

  const handleEdit = () => startTaskChat("Edit");
  const handleFix = () => startTaskChat("Fix");
  const handleRunNow = () => runAction(runAssistantTask, "runAssistantTask", "Task started");
  const handlePause = () => runAction(pauseAssistantTask, "pauseAssistantTask");
  const handleResume = () => runAction(resumeAssistantTask, "resumeAssistantTask");
  const handleDismiss = async () => {
    const { problemAt } = task;
    if (problemAt) {
      await runAction((id) => dismissAssistantTaskProblem(id, { problemAt }), "dismissAssistantTaskProblem");
    }
  };

  return (
    <>
      <GeneralPageHeader
        showDefaultBackButtonTo={getChatPath(clientCode)}
        TitleComponent={<TaskTitle task={task} onRenamed={refresh} />}
      >
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" disabled={busy} onClick={handleEdit}>
            Edit
          </Button>
          {task.status !== "Finished" && (
            <Button variant="contained" disabled={busy || task.isRunning} onClick={handleRunNow}>
              Run now
            </Button>
          )}
          {task.status === "Active" && (
            <Button variant="outlined" disabled={busy} onClick={handlePause}>
              Pause
            </Button>
          )}
          {task.status === "Paused" && (
            <Button variant="outlined" disabled={busy} onClick={handleResume}>
              Resume
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={busy}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </GeneralPageHeader>

      <Box sx={{ overflow: "auto", flex: 1 }}>
        <Stack spacing={3} sx={{ maxWidth: "lg", mx: "auto", p: 3 }}>
          <TaskAttentionBox
            task={task}
            preview={details.latestRunPreview}
            chatPath={task.lastThreadId && getChatPath(clientCode, task.lastThreadId)}
            actionsDisabled={busy}
            onFix={handleFix}
            onRunNow={handleRunNow}
            onResume={handleResume}
            onDismiss={handleDismiss}
            onDelete={() => setDeleteOpen(true)}
          />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <TaskFacts details={details} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <TaskRuns
                runs={details.runs}
                latestRunPreview={
                  details.runs[0]?.threadId === task.lastThreadId ? details.latestRunPreview : undefined
                }
              />
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {deleteOpen && (
        <DeleteTaskDialog
          taskId={task.taskId}
          onCancel={() => setDeleteOpen(false)}
          onDeleted={() => navigate(getChatPath(clientCode), { replace: true })}
        />
      )}
    </>
  );
};

const AssistantTaskPage = () => {
  const { taskId = "" } = useParams();

  return <TaskPageContent key={taskId} taskId={taskId} />;
};

export default AssistantTaskPage;
